import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { collectPassport } from '../stol/passport';
import { useAuth } from '../auth/useAuth';
import { sendReport } from './api';
import { kogda } from './slova';
import { imyaEkrana, snyatEkran, versiyaPaneli, vKartinku, type Metka, type Snimok } from './snimok';
import { useSupportForm } from './store';
import './support.css';

type Sostoyanie =
  | { vid: 'forma' }
  | { vid: 'otpravka' }
  | { vid: 'prinyato'; nomer: number | null; outcome: string | null }
  | { vid: 'oshibka'; tekst: string };

/**
 * Окно «Сообщить о проблеме» (`s737`, макет принят владельцем 10.10.2026).
 * Живёт над маршрутами: работать можно дальше, окно страницу не блокирует.
 * Снимок — страницы панели в момент открытия окна, телефоны клиентов на нём
 * закрыты; автора, роль и последние действия сервер ставит сам.
 */
export function SupportReportForm() {
  const { user } = useAuth();
  const open = useSupportForm((s) => s.open);
  if (!user || !open) return null;
  return createPortal(<Okno />, document.body);
}

function Okno() {
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const text = useSupportForm((s) => s.text);
  const setText = useSupportForm((s) => s.setText);
  const setOpen = useSupportForm((s) => s.setOpen);

  // Экран, время и снимок — того места, где человек нажал кнопку; переход дальше их не меняет.
  const [ekran] = useState(() => ({ put: `${pathname}${search}`.slice(0, 190), imya: imyaEkrana(pathname), vremya: new Date() }));
  const [snimok, setSnimok] = useState<Snimok | null>(null);
  const [snimayu, setSnimayu] = useState(true);
  const [bezSnimka, setBezSnimka] = useState(false);
  const [vydelyayu, setVydelyayu] = useState(false);
  const [metka, setMetka] = useState<Metka | null>(null);
  const [sost, setSost] = useState<Sostoyanie>({ vid: 'forma' });
  // Один ключ на одно сообщение: повтор отправки после сбоя сети второе обращение не создаёт.
  const klyuch = useRef<string>(crypto.randomUUID());

  useEffect(() => {
    let zhiv = true;
    snyatEkran()
      .then((s) => { if (zhiv) setSnimok(s); })
      .catch(() => { if (zhiv) setSnimok(null); })
      .finally(() => { if (zhiv) setSnimayu(false); });
    return () => { zhiv = false; };
  }, []);

  const prevyu = useMemo(() => (snimok ? snimok.canvas.toDataURL('image/jpeg', 0.7) : null), [snimok]);
  const proporciya = snimok ? snimok.canvas.width / Math.max(1, snimok.canvas.height) : 1.6;

  const zakryt = () => setOpen(false);

  const otpravit = async () => {
    if (text.trim().length < 3 || sost.vid === 'otpravka') return;
    setSost({ vid: 'otpravka' });
    const passport = collectPassport();
    try {
      const prilozhit = snimok !== null && !bezSnimka;
      const otvet = await sendReport({
        text: text.trim(),
        request_key: klyuch.current,
        screen: ekran.put,
        app_version: versiyaPaneli(),
        client_time: ekran.vremya.toISOString(),
        passport: passport ? { section: passport.section, state: passport.state } : null,
        marked: prilozhit ? metka : null,
        snapshot: prilozhit ? vKartinku(snimok, metka) : null,
      });
      setText('');
      void queryClient.invalidateQueries({ queryKey: ['support', 'my'] });
      setSost({ vid: 'prinyato', nomer: otvet.incident_id, outcome: otvet.outcome });
    } catch {
      setSost({ vid: 'oshibka', tekst: 'Не отправилось — нет связи с сервером. Текст сохранён, нажмите «Отправить» ещё раз.' });
    }
  };

  if (sost.vid === 'prinyato') {
    const vyklyucheno = sost.outcome === 'intake_off';
    return (
      <div className="sup sup-pop" role="dialog" aria-label="Сообщение принято" data-testid="support-form">
        <div className={`sup-toast${vyklyucheno ? ' sup-toast-warn' : ''}`}>
          <span className="sup-ic sup-ic-y">✓</span>
          <div>
            {vyklyucheno ? (
              <><b>Сообщение сохранено.</b> Приём техподдержки сейчас выключен руководителем — Семён разберёт его, когда приём включат.</>
            ) : (
              <><b>Принято{sost.nomer ? `, №${sost.nomer}` : ''}.</b> Семён уже разбирается. Ответ придёт сюда и в Telegram.</>
            )}
          </div>
        </div>
        <div className="sup-row">
          <button type="button" className="sup-btn sup-pri sup-grow" onClick={() => { zakryt(); navigate('/support/my'); }}>Мои сообщения</button>
          <button type="button" className="sup-btn" onClick={zakryt}>Закрыть</button>
        </div>
      </div>
    );
  }

  return (
    <div className="sup sup-pop" role="dialog" aria-label="Сообщить о проблеме" data-testid="support-form">
      <div className="sup-row sup-pop-head">
        <h3 className="sup-grow">Сообщить о проблеме</h3>
        <button type="button" className="sup-link" onClick={() => { zakryt(); navigate('/support/my'); }}>Мои сообщения</button>
      </div>
      <p className="sup-small sup-muted">Напишите, что не получается. Семён разберётся и ответит здесь и в Telegram.</p>
      <div className="sup-mt12">
        <textarea
          rows={3}
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Например: нажимаю «Записать» — кнопка крутится и ничего не происходит"
          maxLength={4000}
          autoFocus
          data-testid="support-text"
        />
      </div>

      {!bezSnimka && (
        <Prevyu
          prevyu={prevyu}
          snimayu={snimayu}
          skryto={snimok?.skryto ?? 0}
          proporciya={proporciya}
          vydelyayu={vydelyayu}
          metka={metka}
          onMetka={(m) => { setMetka(m); setVydelyayu(false); }}
        />
      )}
      <div className="sup-row sup-wrap sup-small">
        {!bezSnimka && prevyu && (
          <button type="button" className="sup-btn sup-ghost sup-btn-s" onClick={() => { setVydelyayu(!vydelyayu); if (!vydelyayu) setMetka(null); }}>
            {vydelyayu ? 'Отменить выделение' : metka ? '✎ Выделить заново' : '✎ Выделить место'}
          </button>
        )}
        {(prevyu || bezSnimka) && (
          <button type="button" className="sup-btn sup-ghost sup-btn-s" onClick={() => { setBezSnimka(!bezSnimka); setVydelyayu(false); }}>
            {bezSnimka ? 'Приложить снимок экрана' : 'Без снимка'}
          </button>
        )}
      </div>

      <div className="sup-tiny sup-mt8">Приложится само:</div>
      <div className="sup-auto">
        <span>{kogda(ekran.vremya.toISOString()).replace('сегодня, ', '')}</span>
        <span>Экран: {ekran.imya}</span>
        <span>Версия панели</span>
        <span>Последние действия на экране</span>
      </div>

      {sost.vid === 'oshibka' && <p className="sup-err" role="alert">{sost.tekst}</p>}
      <div className="sup-row">
        <button type="button" className="sup-btn sup-pri sup-grow" onClick={otpravit} disabled={text.trim().length < 3 || sost.vid === 'otpravka'} data-testid="support-send">
          {sost.vid === 'otpravka' ? 'Отправляю…' : 'Отправить'}
        </button>
        <button type="button" className="sup-btn" onClick={zakryt}>Отмена</button>
      </div>
      <p className="sup-tiny sup-mt10">Работать можно дальше — окно не мешает и ничего не блокирует.</p>
    </div>
  );
}

interface PrevyuProps {
  prevyu: string | null;
  snimayu: boolean;
  skryto: number;
  proporciya: number;
  vydelyayu: boolean;
  metka: Metka | null;
  onMetka: (m: Metka | null) => void;
}

/** Снимок экрана: свёрнутая полоска, а при выделении — весь снимок, по которому тянут рамку. */
function Prevyu({ prevyu, snimayu, skryto, proporciya, vydelyayu, metka, onMetka }: PrevyuProps) {
  const box = useRef<HTMLDivElement | null>(null);
  const [tyanu, setTyanu] = useState<{ x0: number; y0: number; x1: number; y1: number } | null>(null);

  if (!prevyu) {
    return (
      <div className="sup-snap sup-snap-pusto">
        <div className="sup-tag">{snimayu ? 'Делаю снимок этого экрана…' : 'Снимок экрана сделать не удалось — сообщение уйдёт без него'}</div>
      </div>
    );
  }

  const tochka = (e: ReactPointerEvent) => {
    const r = box.current!.getBoundingClientRect();
    return {
      x: Math.min(1, Math.max(0, (e.clientX - r.left) / r.width)),
      y: Math.min(1, Math.max(0, (e.clientY - r.top) / r.height)),
    };
  };
  const ramka = tyanu
    ? { x: Math.min(tyanu.x0, tyanu.x1), y: Math.min(tyanu.y0, tyanu.y1), w: Math.abs(tyanu.x1 - tyanu.x0), h: Math.abs(tyanu.y1 - tyanu.y0) }
    : metka;
  // Свёрнутая полоска показывает верх снимка; рамку на ней рисуем только на развёрнутом.
  const razvernut = vydelyayu || metka !== null;

  return (
    <div
      ref={box}
      className={`sup-snap${razvernut ? ' sup-snap-full' : ''}${vydelyayu ? ' sup-snap-mark' : ''}`}
      style={razvernut ? { aspectRatio: String(proporciya) } : undefined}
      onPointerDown={vydelyayu ? (e) => { e.currentTarget.setPointerCapture(e.pointerId); const p = tochka(e); setTyanu({ x0: p.x, y0: p.y, x1: p.x, y1: p.y }); } : undefined}
      onPointerMove={vydelyayu && tyanu ? (e) => { const p = tochka(e); setTyanu({ ...tyanu, x1: p.x, y1: p.y }); } : undefined}
      onPointerUp={vydelyayu && tyanu ? () => {
        const gotovo = ramka && ramka.w > 0.03 && ramka.h > 0.03 ? ramka : null;
        setTyanu(null);
        if (gotovo) onMetka(gotovo);
      } : undefined}
      data-testid="support-snap"
    >
      <img src={prevyu} alt="Снимок экрана" draggable={false} />
      {razvernut && ramka && (
        <div className="sup-mark" style={{ left: `${ramka.x * 100}%`, top: `${ramka.y * 100}%`, width: `${ramka.w * 100}%`, height: `${ramka.h * 100}%` }} />
      )}
      <div className="sup-tag">
        {vydelyayu ? 'Обведите место пальцем или мышью' : `Снимок этого экрана${skryto > 0 ? ' · телефон клиента скрыт' : ''}`}
      </div>
    </div>
  );
}
